import { useEffect, useState } from 'react'

import { useErrorBoundary } from 'react-error-boundary'

import { RegisterPageOptions, ShouldPageRenderHook } from './types'

import { AppDependencyUnrecoverableError } from '@/errors/classes/AppDependencyUnrecoverableError'
import { UnrecoverableError } from '@/errors/classes/UnrecoverableError'
import {
  setAnalyticsConfig,
  setPageSuffix,
} from '@/pages/UnrecoverableError/store'

export function createUseShouldRender({
  shouldRenderFactory,
  suffix,
  analyticsConfig,
}: RegisterPageOptions): ShouldPageRenderHook {
  const resolveShouldRender = async () => {
    if (!shouldRenderFactory) return true

    const { shouldRender } = await shouldRenderFactory()
    const shouldRenderResult = shouldRender()

    return Boolean(
      shouldRenderResult instanceof Promise
        ? await shouldRenderResult
        : shouldRenderResult,
    )
  }

  /**
   * The startup of the unrecoverable error store should be done as soon as a page
   * is confirmed to be rendered, so that if an error happens during the validation,
   * the unrecoverable error page can be shown with the correct suffix and analytics config
   */
  const initializeUnrecoverableErrorStore = () => {
    setPageSuffix(suffix)
    setAnalyticsConfig(
      typeof analyticsConfig === 'function'
        ? analyticsConfig()
        : analyticsConfig,
    )
  }

  return (isOptional: boolean) => {
    const [shouldRender, setShouldRender] = useState<boolean>()
    const { showBoundary } = useErrorBoundary<UnrecoverableError>()

    useEffect(() => {
      resolveShouldRender()
        .then((result) => {
          if (result) initializeUnrecoverableErrorStore()
          setShouldRender(result)

          /**
           * {@link PageComponentProps.isOptional}
           */
          if (!result && !isOptional) {
            initializeUnrecoverableErrorStore()
            showBoundary(new AppDependencyUnrecoverableError())
          }
        })
        .catch((error: unknown) => {
          initializeUnrecoverableErrorStore()
          if (error instanceof UnrecoverableError) return showBoundary(error)

          showBoundary(new AppDependencyUnrecoverableError(undefined, error))
        })
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return shouldRender
  }
}
